import { salonTime } from "@shared";
import { useStore } from "@stores";
import { Dayjs } from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useResolvedDates } from "./useResolvedDates";

interface IProps {
  workerId: string | number | undefined;
  tenantId: string | undefined;
  maxBookingDaysAhead?: number;
  requiredMinutes?: number;
}

export const useDisabledDate = ({
  workerId,
  tenantId,
  maxBookingDaysAhead,
  requiredMinutes,
}: IProps) => {
  const { t } = useTranslation();
  const { tenantStore } = useStore();
  const timeZone = tenantStore.tenant?.timeZone;
  const { resolvedDates, loading } = useResolvedDates({
    workerId,
    tenantId,
    maxBookingDaysAhead,
    requiredMinutes,
  });

  const unavailableSources = useMemo(() => {
    const map = new Map<string, string>();
    resolvedDates
      .filter((d) => !d.isAvailable)
      .forEach((d) => map.set(d.date.slice(0, 10), d.source));
    return map;
  }, [resolvedDates]);

  const fullyBookedDates = useMemo(() => {
    const set = new Set<string>();
    if (!requiredMinutes || requiredMinutes <= 0) return set;
    resolvedDates
      .filter((d) => d.isAvailable && d.hasCapacity === false)
      .forEach((d) => set.add(d.date.slice(0, 10)));
    return set;
  }, [resolvedDates, requiredMinutes]);

  // Compared as "YYYY-MM-DD" keys rather than instants: the calendar cells are plain
  // days and the boundaries are the salon's, so keeping both as day strings avoids
  // mixing the visitor's clock with the salon's.
  const todayKey = useMemo(
    () => salonTime(undefined, timeZone).format("YYYY-MM-DD"),
    [timeZone],
  );

  const maxAllowedKey = useMemo(() => {
    if (!maxBookingDaysAhead || maxBookingDaysAhead <= 0) return null;
    return salonTime(undefined, timeZone)
      .add(maxBookingDaysAhead, "day")
      .format("YYYY-MM-DD");
  }, [maxBookingDaysAhead, timeZone]);

  const disabledDate = (current: Dayjs | null): boolean => {
    if (!current) return false;

    const key = current.format("YYYY-MM-DD");

    if (key < todayKey) return true;
    if (maxAllowedKey && key > maxAllowedKey) return true;
    if (unavailableSources.has(key)) return true;
    if (fullyBookedDates.has(key)) return true;

    return false;
  };

  const getDisabledReason = (current: Dayjs | null): string | null => {
    if (!current) return null;

    const key = current.format("YYYY-MM-DD");

    if (key < todayKey) return t("PastDateHint");
    if (maxAllowedKey && key > maxAllowedKey) return t("MaxBookingHint");

    const source = unavailableSources.get(key);
    if (source) {
      if (source === "Unavailability") return t("UnavailabilityHint");
      return t("NonWorkingDayHint");
    }

    if (fullyBookedDates.has(key)) return t("AllSlotsBooked");

    return null;
  };

  return { disabledDate, getDisabledReason, loading };
};
